import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Collapse,
  Chip,
  TablePagination,
  Typography,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { getDeductionBreakdownColumns, DEDUCTION_APPROVAL_COLUMNS } from "../../../Data/Configuration/DeductionApproval";
import { GREEN_LINK_STYLE, STATUS_CHIP_STYLES } from "../../../styles/CommonStyles";
import { getPendingSettlements, getSettlementDetails } from "../../../Services/DriverDeduction.service";

/**
 * Expandable row component — lazily loads per-driver settlement details
 */
const ExpandableRow = ({ row, isSelected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsFetched, setDetailsFetched] = useState(false);
  const breakdownColumns = getDeductionBreakdownColumns();

  const handleToggle = useCallback(async () => {
    if (detailsLoading) return;
    const nextOpen = !open;
    setOpen(nextOpen);
    // Lazy load: fetch details only on first expand
    if (nextOpen && !detailsFetched) {
      setDetailsLoading(true);
      try {
        const resp = await getSettlementDetails(row.driverId, {
          fromDate: row.periodFrom,
          toDate: row.periodTo,
          page: 0,
          size: 50,
        });
        setDetails(resp?.data?.list || []);
      } catch (e) {
        console.error("Failed to load settlement details", e);
      } finally {
        setDetailsLoading(false);
        setDetailsFetched(true);
      }
    }
  }, [open, detailsFetched, row]);

  const innerHeaderRowStyle = {
    "& th": {
      color: "#626262",
      fontWeight: "600",
      fontSize: "1vw",
      height: "2.8vw",
      paddingX: "0.7vw",
      paddingY: "0.5vw",
      backgroundColor: "rgba(230, 230, 230, 1)",
      borderBottom: "none",
    },
  };

  const innerBodyRowStyle = {
    "& td": {
      fontSize: "0.95vw",
      paddingX: "0.7vw",
      paddingY: "0.7vw",
      borderBottom: "none",
      color: "#313131",
    },
  };

  return (
    <>
      {/* Main Row */}
      <TableRow
        sx={{
          "&:hover": { backgroundColor: "#EBEBEB" },
          "& td": {
            fontSize: "1vw",
            paddingX: "0.7vw",
            paddingY: "0.8vw",
            borderBottom: "none",
            color: "#313131",
          },
        }}
      >
        {/* Checkbox + Arrow + Driver Name — one cell */}
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}>
            <Checkbox
              checked={isSelected}
              onChange={() => onSelect(row.driverId)}
              sx={{
                color: "#D9D9D9",
                padding: "0.3vw",
                "&.Mui-checked": { color: "#39B54A" },
              }}
            />
            <IconButton size="small" onClick={handleToggle} sx={{ padding: "0.15vw" }}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <Box sx={GREEN_LINK_STYLE}>{row.driverName}</Box>
          </Box>
        </TableCell>

        <TableCell sx={{ fontWeight: "bold" }}>{row.cabNumber}</TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>
          {row.periodFrom} – {row.periodTo}
        </TableCell>
        <TableCell>
          {row.currentWalletBalance != null ? row.currentWalletBalance.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }) : "—"}
        </TableCell>
        <TableCell>
          {row.totalAmount != null ? row.totalAmount.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }) : "—"}
        </TableCell>
        <TableCell>
          <Chip
            label={row.settlementAction}
            size="small"
            sx={{
              backgroundColor: row.settlementAction === "Credit" ? "#E6F8E8" : "#FFF3E0",
              color: row.settlementAction === "Credit" ? "#2E7D32" : "#E65100",
              fontWeight: 600,
              fontSize: "0.78vw",
            }}
          />
        </TableCell>
        <TableCell>
          {row.expectedBalance != null ? row.expectedBalance.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }) : "—"}
        </TableCell>
        <TableCell>
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: "0.6vw !important" }} />}
            label={row.settlementStatus}
            size="small"
            sx={STATUS_CHIP_STYLES.pending}
          />
        </TableCell>
      </TableRow>

      {/* Expanded Row — settlement breakdown */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                marginLeft: "3.5vw",
                marginY: "0.5vw",
                border: "1px solid rgba(217, 217, 217, 1)",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {detailsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : details.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <Typography sx={{ color: "#9e9e9e", fontSize: "0.9vw" }}>No breakdown data available</Typography>
                </Box>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={innerHeaderRowStyle}>
                        {breakdownColumns.map((col) => (
                          <TableCell key={col.id} sx={{ width: col.width }}>{col.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                  </Table>
                  <Box sx={{ maxHeight: "130px", overflowY: "auto" }}>
                    <Table size="small">
                      <TableBody>
                        {details.map((d, i) => (
                          <TableRow key={i} sx={innerBodyRowStyle}>
                            <TableCell sx={{ width: "15%" }}>{d.settlementDate}</TableCell>
                            <TableCell sx={{ width: "15%" }}>{d.deductionType}</TableCell>
                            <TableCell sx={{ width: "15%" }}>
                              {d.deductionAmount != null
                                ? d.deductionAmount.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 })
                                : "—"}
                            </TableCell>
                            <TableCell sx={{ width: "15%" }}>{d.transactionType}</TableCell>
                            <TableCell sx={{ width: "15%" }}>{d?.tripCount || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Deduction Approval Tab — server-side paginated, real API.
 * Props:
 *   searchQuery    — driver name search string
 *   serviceCityId  — from global city selector
 *   selectedRows   — array of selected driverIds
 *   selectAll      — boolean, true when header checkbox is fully checked
 *   onSelectionChange(driverIds, selectAll) — lifts selection up
 */
const DeductionApprovalTab = ({
  searchQuery = "",
  serviceCityId = null,
  selectedRows = [],
  selectAll = false,
  onSelectionChange,
  onTotalCountChange,
  refreshKey = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getPendingSettlements({
        page,
        size: rowsPerPage,
        serviceCityId: serviceCityId || undefined,
        driverName: searchQuery && isNaN(searchQuery) ? searchQuery : undefined,
        cabNumber: searchQuery && !isNaN(searchQuery) ? searchQuery : undefined,
      });
      const list = resp?.data?.list || [];
      const count = resp?.data?.count || 0;
      setTableData(list);
      setTotalCount(count);
      onTotalCountChange?.(count);
    } catch (e) {
      console.error("Failed to fetch pending settlements", e);
      setTableData([]);
      setTotalCount(0);
      onTotalCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, serviceCityId, searchQuery, refreshKey, onTotalCountChange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [serviceCityId, searchQuery, refreshKey]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const rows = tableData.map(({ driverId, periodFrom, periodTo }) => ({ driverId, periodFrom, periodTo }));
      onSelectionChange?.(rows, true);
    } else {
      onSelectionChange?.([], false);
    }
  };

  const handleSelectRow = (driverId) => {
    const row = tableData.find((r) => r.driverId === driverId);
    const newSelection = selectedIds.has(driverId)
      ? selectedRows.filter((r) => r.driverId !== driverId)
      : [...selectedRows, { driverId: row.driverId, periodFrom: row.periodFrom, periodTo: row.periodTo }];
    onSelectionChange?.(newSelection, false);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headerRowStyle = {
    "& th": {
      color: "#626262",
      fontWeight: "700",
      fontSize: "1.11vw",
      height: "3.89vw",
      maxHeight: "5vw",
      paddingX: "0.7vw",
      paddingY: "0.7vw",
      backgroundColor: "#E1F4E4",
    },
  };

  const columns = DEDUCTION_APPROVAL_COLUMNS;
  const allSelected = tableData.length > 0 && selectedRows.length === tableData.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < tableData.length;

  const selectedIds = useMemo(() => new Set(selectedRows.map((r) => r.driverId)), [selectedRows]);

  if (!loading && tableData.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          minHeight: "13.9vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "0.42vw",
        }}
      >
        <Typography sx={{ color: "black" }}>No pending settlements found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "58vh",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={headerRowStyle}>
              {/* First header: Checkbox + Driver Name */}
              <TableCell sx={{ width: columns[0].width }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}>
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    sx={{
                      color: "#D9D9D9",
                      padding: "0.3vw",
                      "&.Mui-checked": { color: "#39B54A" },
                      "&.MuiCheckbox-indeterminate": { color: "#39B54A" },
                    }}
                  />
                  <Box sx={{ textAlign: "left" }}>{columns[0].label}</Box>
                </Box>
              </TableCell>
              {columns.slice(1).map((col) => (
                <TableCell key={col.id} sx={{ width: col.width }}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row) => (
                <ExpandableRow
                  key={row.driverId}
                  row={row}
                  isSelected={selectedIds.has(row.driverId)}
                  onSelect={handleSelectRow}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[15, 25, 50]}
        sx={{
          "& .MuiTablePagination-toolbar": {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            overflowX: "hidden",
          },
          "& .MuiTablePagination-select": {
            backgroundColor: "rgba(255,255,255,1)",
            borderRadius: "0.42vw",
            border: "1px solid #D9D9D9",
          },
          "& .MuiTablePagination-actions button": {
            color: "rgba(112,112,112,1)",
            transform: "scale(1.3)",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            color: "rgba(98,98,98,1)",
            fontSize: "0.97vw",
            textAlign: "center",
            m: "auto",
            letterSpacing: "0.02vw",
          },
        }}
      />
    </Box>
  );
};

export default DeductionApprovalTab;
